import React, { memo, useRef } from 'react';
import { ColorPickerV2 } from '@bynder/design-system';
import type { Color } from 'packages/ds/ColorPicker/types';
import { ColorPickerTrigger, type ColorPickerTriggerRef } from './ColorPickerTrigger';
import { ColorPickerLayer } from './ColorPickerLayer';
import { arePropsEqual, type Props, type TriggerRenderProps } from './shared';
import { fromPickerValue, toPickerValue } from './pickerValue';

const SOLID_ONLY = ['solid'] as const;
const ALL_COLOR_TYPES = ['solid', 'linear', 'radial'] as const;

const ColorInputV2 = memo<Props>(
    ({ isMixed, id, value, onChange, label, testId, isDisabled, isGradientDisabled, isLeftPositionAllowed }: Props) => {
        const colorPickerTriggerRef = useRef<ColorPickerTriggerRef | null>(null);

        const onTriggerChange = (newValue: Color) => {
            colorPickerTriggerRef.current?.setValue(newValue);
            onChange(newValue);
        };

        const onPickerChange = (newValue: Color) => onTriggerChange(fromPickerValue(newValue));

        // Only the ref and the aria attributes are forwarded. DS builds its trigger props from
        // floating-ui's getReferenceProps(), whose key handlers test the reference element rather
        // than the event target, so spreading them would let Space in the hex input open the picker.
        const renderTrigger = ({ onClick, ref, ...triggerProps }: TriggerRenderProps) => (
            <ColorPickerTrigger
                ref={colorPickerTriggerRef}
                onClick={onClick}
                onChange={onTriggerChange}
                triggerRef={ref}
                triggerProps={{
                    'aria-haspopup': triggerProps['aria-haspopup'],
                    'aria-expanded': triggerProps['aria-expanded'],
                    'aria-controls': triggerProps['aria-controls'],
                }}
                defaultValue={value}
                isMixed={isMixed}
                id={id}
                label={label}
                testId={testId}
                isDisabled={isDisabled}
            />
        );

        const pickerValue = toPickerValue(value);
        const types = isGradientDisabled ? SOLID_ONLY : ALL_COLOR_TYPES;

        if (isLeftPositionAllowed) {
            return (
                <ColorPickerLayer value={pickerValue} onChange={onPickerChange} types={types} trigger={renderTrigger} />
            );
        }

        return <ColorPickerV2 value={pickerValue} onChange={onPickerChange} types={types} trigger={renderTrigger} />;
    },
    arePropsEqual,
);

export default ColorInputV2;
